"use client";

import { useEffect, useRef, useState } from "react";

/**
 * FactoryCinema — full-bleed editorial photo band.
 *
 * Four Moss Vale workshop photos auto-advance every 7s with a slow
 * Ken-Burns push / pan / rise motion per plate. Crossfade dissolves
 * between frames. Pauses when the section scrolls out of view.
 *
 * Per the user's brief, there is intentionally no overlay copy on the
 * photos — only the four thin progress segments at the bottom-right
 * hint at the cycle.
 *
 * The CSS animations don't auto-restart when the `.is-active` class
 * re-applies to a previously-seen frame, so on each cycle we force a
 * reflow on the active <img> and the active progress bar to replay the
 * keyframes cleanly. The same trick the vanilla design uses.
 */
// Photos converted to WebP via scripts/factory_to_webp.py — Lighthouse flagged
// ~783KB of savings vs the JPEG originals (60% smaller, same visual quality).
const FRAMES = [
  {
    src: "/factory/factory-1.webp",
    motion: "push" as const,
    alt: "Moss Vale production floor",
  },
  {
    src: "/factory/factory-2.webp",
    motion: "pan-right" as const,
    alt: "Hot-water tanks on the paint conveyor",
  },
  {
    src: "/factory/factory-3.webp",
    motion: "push" as const,
    alt: "Operator at the welding cell",
  },
  {
    src: "/factory/factory-4.webp",
    motion: "rise" as const,
    alt: "Technician inspecting an element fitting",
  },
];

// 7s per plate; matches @keyframes cine-bar duration in landing.css so
// the progress segment fills in lockstep with the dwell time.
const DURATION_MS = 7000;

export default function FactoryCinema() {
  const [active, setActive] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);
  const barsRef = useRef<HTMLDivElement>(null);

  // Force keyframe restart on the currently-active <img> and progress
  // bar each time `active` changes. Without this, after the first cycle
  // a frame that becomes active again would keep its end-state transform
  // (scaled-in) with no motion, and the progress bar would jump to full.
  useEffect(() => {
    const stage = stageRef.current;
    const bars = barsRef.current;
    if (!stage || !bars) return;

    const frame = stage.children[active] as HTMLElement | undefined;
    const img = frame?.querySelector("img") ?? null;
    if (img) {
      img.style.animation = "none";
      // Reading offsetWidth flushes layout so the next assignment registers
      // as a fresh animation rather than a no-op.
      void img.offsetWidth;
      img.style.animation = "";
    }

    const bar = bars.children[active] as HTMLElement | undefined;
    if (bar) {
      bar.classList.remove("is-active");
      void bar.offsetWidth;
      bar.classList.add("is-active");
    }
  }, [active]);

  // Auto-advance + pause-when-offscreen. Bootstrap immediately so the
  // first plate animates whether or not the cinema is in view at load.
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (timer) return;
      timer = setInterval(
        () => setActive((i) => (i + 1) % FRAMES.length),
        DURATION_MS
      );
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    start();

    const node = stageRef.current;
    if (!node) return stop;

    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => (entry.isIntersecting ? start() : stop())),
      { threshold: 0 }
    );
    io.observe(node);

    return () => {
      stop();
      io.disconnect();
    };
  }, []);

  return (
    <section className="cinema" aria-label="Inside the Moss Vale workshop">
      <div className="cinema-stage" ref={stageRef}>
        {FRAMES.map((f, i) => (
          <div
            key={f.src}
            className={`cinema-frame${i === active ? " is-active" : ""}`}
            data-motion={f.motion}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={f.src}
              alt={f.alt}
              loading="lazy"
              width={1500}
              height={1000}
            />
          </div>
        ))}
      </div>

      <div className="cinema-vignette" aria-hidden="true" />

      <div className="cinema-chrome">
        <div className="cinema-bars" ref={barsRef}>
          {FRAMES.map((_, i) => (
            <span
              key={i}
              className={
                "cinema-bar" +
                (i === active
                  ? " is-active"
                  : i < active
                    ? " is-played"
                    : "")
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
}
